import { CurrencyType } from 'features/Programs/Shared/types'

export interface ConversionRates {
  usdCadConversionRate?: number
  usdPlnConversionRate?: number
  usdEurConversionRate?: number
  usdUahConversionRate?: number
  usdGbpConversionRate?: number
  usdJpyConversionRate?: number
  usdSgdConversionRate?: number
}

/**
 * Returns the display symbol for a currency code without formatting/converting any amount.
 * @param currencyCode - The target currency code (USD, CAD, EUR, PLN, UAH, GBP, JPY, SGD)
 * @returns Currency symbol (e.g., "$", "CA$", "€", "zł", "₴", "£", "¥", "S$")
 */
export function getCurrencySymbol(currencyCode: CurrencyType): string {
  let locale = 'en-US'
  let currency = 'USD'

  switch (currencyCode) {
    case 'CAD':
      locale = 'en-CA'
      currency = 'CAD'
      break
    case 'EUR':
      locale = 'de-DE'
      currency = 'EUR'
      break
    case 'PLN':
      locale = 'pl-PL'
      currency = 'PLN'
      break
    case 'UAH':
      locale = 'uk-UA'
      currency = 'UAH'
      break
    case 'GBP':
      locale = 'en-GB'
      currency = 'GBP'
      break
    case 'JPY':
      locale = 'ja-JP'
      currency = 'JPY'
      break
    case 'SGD':
      locale = 'en-SG'
      currency = 'SGD'
      break
    case 'USD':
    default:
      locale = 'en-US'
      currency = 'USD'
  }

  try {
    const symbol = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value

    return symbol || '$'
  } catch {
    return '$'
  }
}

/**
 * Converts USD amount to target currency and formats it with currency symbol
 * @param currencyCode - The target currency code (USD, CAD, EUR, PLN, UAH, GBP, JPY, SGD)
 * @param usdAmount - The amount in USD (in cents, will be divided by 100)
 * @param conversionRates - Object containing conversion rates from Redux state
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted currency string with symbol (e.g., "$1,234.56", "€1,234.56", "zł1,234.56", "CA$1,234.56")
 */
export function formatCurrency(
  currencyCode: CurrencyType,
  usdAmount: number,
  conversionRates: ConversionRates,
  decimals: number = 2
): string {
  // Convert from cents to dollars
  const usdAmountInDollars = usdAmount / 100

  // If USD, no conversion needed
  if (currencyCode === 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(usdAmountInDollars)
  }

  // Get conversion rate based on currency
  let conversionRate: number | undefined
  let locale: string
  let currency: string

  switch (currencyCode) {
    case 'CAD':
      conversionRate = conversionRates.usdCadConversionRate
      locale = 'en-CA'
      currency = 'CAD'
      break
    case 'EUR':
      conversionRate = conversionRates.usdEurConversionRate
      locale = 'de-DE' // German locale for EUR formatting
      currency = 'EUR'
      break
    case 'PLN':
      conversionRate = conversionRates.usdPlnConversionRate
      locale = 'pl-PL'
      currency = 'PLN'
      break
    case 'UAH':
      conversionRate = conversionRates.usdUahConversionRate
      locale = 'uk-UA'
      currency = 'UAH'
      break
    case 'GBP':
      conversionRate = conversionRates.usdGbpConversionRate
      locale = 'en-GB'
      currency = 'GBP'
      break
    case 'JPY':
      conversionRate = conversionRates.usdJpyConversionRate
      locale = 'ja-JP'
      currency = 'JPY'
      break
    case 'SGD':
      conversionRate = conversionRates.usdSgdConversionRate
      locale = 'en-SG'
      currency = 'SGD'
      break
    default:
      // Fallback to USD if currency not recognized
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(usdAmountInDollars)
  }

  // If conversion rate is not available, return formatted USD
  if (!conversionRate || conversionRate <= 0) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(usdAmountInDollars)
  }

  // Convert USD to target currency
  const convertedAmount = usdAmountInDollars * conversionRate
  const fractionDigits = currency === 'JPY' ? 0 : decimals

  // Format with proper locale and currency symbol
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(convertedAmount)
}
